import { Base, H2, H3, Lead, Paragraph } from '@sbab-ui/components'
import { Suggestion } from '../../api/httpHandlers'
import ConsumptionGraphic from './ConsumptionGraphic'
import SlideOver from './SlideOver'
import { getInvestmentCostCopyById } from './investmentCostList'

interface Props {
  suggestion: Suggestion | ''
  totalConsumption?: number
}

export default function SavingsEstimation({ suggestion, totalConsumption }: Readonly<Props>) {
  if (!suggestion)
    return (
      <div
        className="mb-10 border !border-neutral-60 px-4 py-8 md:w-1/2"
        aria-label="Besparingsprognos för valda åtgärder"
      >
        <div className="flex flex-col gap-4">
          <H2 size="h3">Besparingsprognos för vald åtgärd</H2>
          <Lead className="!m-0">Välj en åtgärd för att se effekten på din förbrukning</Lead>
        </div>
      </div>
    )

  const { yearly_savings, price_per_kWh, yearly_savings_kWh } = suggestion
  const investmentCostCopy = getInvestmentCostCopyById(suggestion.id)

  return (
    <div className="border !border-neutral-60 px-[24px] py-8 md:w-1/2">
      <div className="flex flex-col gap-8">
        <H3 className="!mt-3">Besparingsprognos för vald åtgärd</H3>
        {investmentCostCopy && (
          <div className="flex flex-col gap-2">
            <Lead className="!mt-0">Investeringskostnad</Lead>
            <Paragraph className="!mt-0">{investmentCostCopy}</Paragraph>
          </div>
        )}
        <ConsumptionGraphic yearlySavingsKWh={yearly_savings_kWh} userConsumption={totalConsumption} />
        <div>
          <Lead className="!mb-2">Så här mycket kan du spara*</Lead>
          <H2 className="!mt-0" as="p">
            {Math.round(yearly_savings / 12).toLocaleString('sv-SE')} kr/mån
          </H2>
        </div>
        {suggestion.key === 'solar_panel_power' ? (
          <Base className="!-mb-5 text-neutral-40">
            * Indikation baserat på det kommande årets elprisprognos för ditt område på{' '}
            {price_per_kWh.toLocaleString('sv-SE')} kr/kWh
          </Base>
        ) : (
          <Base className="!-mb-5 text-neutral-40">
            * Indikation baserat på det kommande årets elprisprognos för ditt område på{' '}
            {price_per_kWh.toLocaleString('sv-SE')} kr/kWh.
          </Base>
        )}
        <SlideOver title="Mer om hur vi räknat" showTitle={true}>
          <H3 className="!mb-2 !font-normal">Investeringskostnad</H3>
          <Paragraph className="!mb-6">
            <p>
              Det här är en ungefärlig kostnad som bygger på antaganden och ska ses som en
              uppskattning. Investeringskostnaden beror på olika faktorer och förutsättningar för
              ditt hus.
            </p>
          </Paragraph>
          <H3 className="!mb-2 !mt-10 !font-normal">Uppskattad total förbrukning </H3>
          <Paragraph>
            <p>
              Graferna visar en uppskattad förbrukning innan och efter vald energiåtgärd. Den
              uppskattade förbrukningen bygger på statistik från Booli och energimyndigheten.
            </p>
            <p className="!mt-2">Förbrukningen utgörs av värme, varmvatten och hushållsel.</p>
          </Paragraph>
          <H3 className="!mb-2 !mt-10 !font-normal">Så här mycket kan du spara</H3>
          <Paragraph>
            <p>
              Hur mycket du kan spara beräknas på uppskattad förbrukning efter genomförd
              energiåtgärd och elprisprognos.{' '}
            </p>
          </Paragraph>
          <H3 className="!mb-2 !mt-10 !font-normal">Elprisprognos</H3>
          <Paragraph>
            <p>
              Priset är Boolis prognos av kommande års elpriser och kan skilja sig mellan olika
              energiåtgärder.
            </p>
            <p className="!mt-2">
              Till exempel producerar solceller mer el på sommaren när elpriserna generellt är
              lägre. Värmekällan sparar mest energi på vintern när priserna är höga. Sänker du
              temperaturen så spar du energi ganska jämnt fördelad under hela året.
            </p>
            <p className="!mt-2">
              Detta gör att priset per kWh för solceller eller sänkt temperatur kan vara ett annat
              än för värmekälla.
            </p>
          </Paragraph>
        </SlideOver>
      </div>
    </div>
  )
}
